import { useMemo, useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Play,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react'
import ErrorBanner from '@/components/ErrorBanner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import {
  useRunSource,
  useSources,
  useUpdateSource,
} from '@/hooks/useSources'
import { formatTime, relativeTime, cn } from '@/lib/utils'
import {
  Badge,
  Button,
  Card,
  Input,
  Select,
  Toggle,
} from '@/admin-design/_common'
import type { Source } from '@/api/types'

// ─── Grouping ───────────────────────────────────────────────────────────

interface SourceGroup {
  name: string
  sources: Source[]
}

function groupByKind(sources: Source[]): SourceGroup[] {
  const map = new Map<string, Source[]>()
  for (const s of sources) {
    const g = s.kind || 'other'
    if (!map.has(g)) map.set(g, [])
    map.get(g)!.push(s)
  }
  return [...map.entries()]
    .map(([name, sources]) => ({ name, sources: sources.sort((a, b) => a.slug.localeCompare(b.slug)) }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// ─── Bulk action bar ────────────────────────────────────────────────────

function BulkBar({
  selected,
  sources,
  onClear,
}: {
  selected: Set<number>
  sources: Source[]
  onClear: () => void
}) {
  const update = useUpdateSource()
  const run = useRunSource()
  const [groupInput, setGroupInput] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [scheduleInput, setScheduleInput] = useState('')

  const ids = [...selected]
  const groups = [...new Set(sources.map((s) => s.kind).filter(Boolean))].sort()

  const bulk = (input: Parameters<typeof update.mutate>[0]['input']) =>
    ids.forEach((id) => update.mutate({ id, input }))

  const moveOptions = [
    { label: 'Move to...', value: '' },
    ...groups.map((g) => ({ label: g, value: g })),
  ]

  return (
    <div className="sticky top-0 z-10 bg-info-soft/95 backdrop-blur border border-info-soft rounded-lg px-3 py-2 space-y-2">
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        <span className="font-medium text-info-soft-foreground mr-1">{ids.length} selected</span>

        <Select
          value=""
          onValueChange={(v) => { if (v) bulk({ kind: v }) }}
          options={moveOptions}
          className="w-auto py-1"
        />
        <Input
          type="text"
          placeholder="new group"
          tone="info"
          block={false}
          className="px-1.5 py-1 w-20"
          value={groupInput}
          onChange={(e) => setGroupInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && groupInput.trim()) {
              bulk({ kind: groupInput.trim() }); setGroupInput('')
            }
          }}
        />

        <span className="w-px h-4 bg-info-soft" />

        <Button type="button" size="sm" onClick={() => bulk({ enabled: true })} className="bg-success text-success-foreground hover:bg-success/90 h-7 px-2 text-[11px]">Enable</Button>
        <Button type="button" variant="destructive" size="sm" onClick={() => bulk({ enabled: false })} className="h-7 px-2 text-[11px]">Disable</Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => ids.forEach((id) => run.mutate(id))} className="h-7 px-2 text-[11px]">
          <Play size={10} /> Run
        </Button>

        <span className="w-px h-4 bg-info-soft" />

        <Input type="text" placeholder="tags" tone="info" block={false} className="px-1.5 py-1 w-24"
          value={tagsInput} onChange={(e) => setTagsInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tagsInput.trim()) {
              bulk({ tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean) }); setTagsInput('')
            }
          }} />
        <Input type="text" placeholder="schedule" title="cron: 분 시 일 월 요일 (e.g. */10 * * * *)" tone="info" block={false} className="px-1.5 py-1 w-28 font-mono"
          value={scheduleInput} onChange={(e) => setScheduleInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && scheduleInput.trim()) {
              bulk({ schedule: scheduleInput.trim() }); setScheduleInput('')
            }
          }} />

        <button type="button" onClick={onClear} className="ml-auto text-info hover:text-info/80">Clear</button>
      </div>
    </div>
  )
}

// ─── Source row ──────────────────────────────────────────────────────────

function SourceRow({
  source: s,
  expanded,
  checked,
  onToggleExpand,
  onToggleCheck,
}: {
  source: Source
  expanded: boolean
  checked: boolean
  onToggleExpand: () => void
  onToggleCheck: () => void
}) {
  const update = useUpdateSource()
  const run = useRunSource()
  const [schedDraft, setSchedDraft] = useState(s.schedule)
  const [schedEditing, setSchedEditing] = useState(false)
  const [paramsDraft, setParamsDraft] = useState('')
  const [paramsEditing, setParamsEditing] = useState(false)

  const statusIcon = s.last_error
    ? <AlertCircle size={14} className="text-destructive" />
    : s.registered
      ? <CheckCircle2 size={14} className="text-success" />
      : <span className="text-[10px] text-warning font-medium">N/A</span>

  return (
    <>
      <div className={cn(
        'flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors border-b border-border last:border-0',
        !s.enabled && 'opacity-50',
      )}>
        {/* checkbox + expand */}
        <input type="checkbox" checked={checked} onChange={onToggleCheck} className="h-3.5 w-3.5 shrink-0" />
        <button type="button" onClick={onToggleExpand} className="text-muted-foreground hover:text-foreground shrink-0">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* slug + display name */}
        <div className="min-w-0 flex-1">
          <span className="font-mono text-xs text-foreground">{s.slug}</span>
          <span className="text-xs text-muted-foreground ml-1.5 hidden sm:inline">{s.display_name}</span>
        </div>

        {/* tags */}
        <div className="hidden md:flex gap-0.5 shrink-0">
          {(s.tags ?? []).slice(0, 3).map((t) => (
            <Badge key={t} className="text-[10px]">{t}</Badge>
          ))}
        </div>

        {/* schedule */}
        {schedEditing ? (
          <input type="text" value={schedDraft} onChange={(e) => setSchedDraft(e.target.value)}
            className="font-mono w-28 shrink-0 px-1.5 py-0.5 text-[11px] h-6 rounded border border-ring bg-card text-foreground outline-none ring-1 ring-ring"
            title="cron: 분 시 일 월 요일 (e.g. */10 * * * *)"
            onBlur={() => {
              if (schedDraft.trim() !== s.schedule) update.mutate({ id: s.id, input: { schedule: schedDraft.trim() } })
              setSchedEditing(false)
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
            autoFocus />
        ) : (
          <button type="button" onClick={() => { setSchedDraft(s.schedule); setSchedEditing(true) }}
            className="font-mono text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted px-1 py-0.5 rounded shrink-0 hidden sm:block"
            title="cron: 분 시 일 월 요일 (클릭하여 수정)">
            {s.schedule}
          </button>
        )}

        {/* enabled toggle */}
        <Toggle
          checked={s.enabled}
          onChange={(v) => update.mutate({ id: s.id, input: { enabled: v } })}
          disabled={update.isPending}
          aria-label={`Toggle ${s.slug}`}
          size="sm"
        />

        {/* last run */}
        <span className="text-[11px] text-muted-foreground tabular-nums w-14 text-right shrink-0 hidden sm:block"
          title={formatTime(s.last_ran_at)}>
          {s.last_ran_at ? relativeTime(s.last_ran_at) : '-'}
        </span>

        {/* status */}
        <span className="shrink-0 w-5 flex justify-center">{statusIcon}</span>

        {/* run */}
        <button type="button" onClick={() => run.mutate(s.id)}
          disabled={run.isPending || !s.registered}
          className="shrink-0 p-1 rounded hover:bg-muted disabled:opacity-30 text-muted-foreground hover:text-foreground">
          <Play size={12} />
        </button>
      </div>

      {/* expanded detail panel */}
      {expanded && (
        <div className="bg-muted/50 border-b border-border px-4 py-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Field label="Display name" defaultValue={s.display_name}
              onSave={(v) => update.mutate({ id: s.id, input: { display_name: v } })} />
            <Field label="Group" defaultValue={s.kind} placeholder="group name"
              onSave={(v) => update.mutate({ id: s.id, input: { kind: v } })} />
            <Field label="Tags" defaultValue={(s.tags ?? []).join(', ')} placeholder="tag1, tag2"
              onSave={(v) => {
                const tags = v.split(',').map((t) => t.trim()).filter(Boolean)
                update.mutate({ id: s.id, input: { tags } })
              }} />
            <div>
              <label className="block text-muted-foreground mb-0.5">Retention</label>
              <div className="flex items-center gap-1">
                <Input type="number" min={1} max={90} defaultValue={s.retention_days}
                  className="w-14 tabular-nums px-1.5 py-1 text-xs h-auto"
                  onBlur={(e) => {
                    const v = parseInt(e.target.value, 10)
                    if (v > 0 && v !== s.retention_days) update.mutate({ id: s.id, input: { retention_days: v } })
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }} />
                <span className="text-muted-foreground">days</span>
              </div>
            </div>
          </div>

          {s.last_error && (
            <div className="mt-2">
              <label className="block text-muted-foreground text-xs mb-0.5">Last error</label>
              <pre className="text-xs text-destructive-soft-foreground bg-destructive-soft border border-destructive-soft rounded p-2 whitespace-pre-wrap break-words max-h-24 overflow-y-auto">
                {s.last_error}
              </pre>
            </div>
          )}

          <div className="mt-2">
            <label className="block text-muted-foreground text-xs mb-0.5">params_json</label>
            {paramsEditing ? (
              <div className="space-y-1">
                <textarea value={paramsDraft} onChange={(e) => setParamsDraft(e.target.value)}
                  rows={6} className="w-full border border-border rounded px-2 py-1 text-xs font-mono bg-card text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring" autoFocus />
                <div className="flex gap-1 justify-end">
                  <Button type="button" variant="secondary" size="sm" onClick={() => setParamsEditing(false)} className="h-7 px-2 text-xs">Cancel</Button>
                  <Button type="button" variant="primary" size="sm" loading={update.isPending} className="h-7 px-2 text-xs"
                    onClick={() => {
                      try { JSON.parse(paramsDraft) } catch { alert('Invalid JSON'); return }
                      update.mutate({ id: s.id, input: { params_json: paramsDraft.trim() } }, { onSuccess: () => setParamsEditing(false) })
                    }}
                  >Save</Button>
                </div>
              </div>
            ) : (
              <button type="button"
                onClick={() => {
                  try { setParamsDraft(JSON.stringify(JSON.parse(s.params_json || '{}'), null, 2)) }
                  catch { setParamsDraft(s.params_json || '{}') }
                  setParamsEditing(true)
                }}
                className="w-full text-left font-mono whitespace-pre-wrap break-words bg-card text-foreground border border-border rounded px-2 py-1.5 text-xs hover:bg-muted/50 cursor-pointer max-h-32 overflow-y-auto">
                {(() => { try { return JSON.stringify(JSON.parse(s.params_json || '{}'), null, 2) } catch { return s.params_json || '{}' } })()}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

/** Reusable inline-edit text field for the detail panel. */
function Field({ label, defaultValue, placeholder, onSave }: {
  label: string; defaultValue: string; placeholder?: string
  onSave: (v: string) => void
}) {
  return (
    <div>
      <label className="block text-muted-foreground mb-0.5">{label}</label>
      <Input type="text" defaultValue={defaultValue} placeholder={placeholder}
        className="px-1.5 py-1 text-xs h-auto"
        onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== defaultValue) onSave(v) }}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }} />
    </div>
  )
}

// ─── Group section ──────────────────────────────────────────────────────

function GroupSection({
  group, expanded, selected, onToggleExpand, onToggleCheck, onToggleGroupCheck,
}: {
  group: SourceGroup
  expanded: Set<number>
  selected: Set<number>
  onToggleExpand: (id: number) => void
  onToggleCheck: (id: number) => void
  onToggleGroupCheck: (ids: number[], checked: boolean) => void
}) {
  const update = useUpdateSource()
  const [collapsed, setCollapsed] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(group.name)
  const ids = group.sources.map((s) => s.id)
  const allChecked = ids.length > 0 && ids.every((id) => selected.has(id))
  const someChecked = ids.some((id) => selected.has(id))
  const enabledCount = group.sources.filter((s) => s.enabled).length
  const errorCount = group.sources.filter((s) => s.last_error).length

  const saveRename = () => {
    const v = draft.trim()
    if (v && v !== group.name) ids.forEach((id) => update.mutate({ id, input: { kind: v } }))
    setRenaming(false)
  }

  return (
    <Card className="overflow-x-auto">
      {/* Group header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/70 border-b border-border">
        <input type="checkbox" checked={allChecked}
          ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked }}
          onChange={(e) => onToggleGroupCheck(ids, e.target.checked)}
          className="h-3.5 w-3.5 shrink-0" />

        <button type="button" onClick={() => setCollapsed((v) => !v)} className="text-muted-foreground hover:text-foreground shrink-0">
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>

        {renaming ? (
          <Input type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
            onBlur={saveRename}
            onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') { setDraft(group.name); setRenaming(false) } }}
            className="px-1.5 py-0.5 text-sm font-medium w-36 h-auto" autoFocus />
        ) : (
          <button type="button" onClick={() => { setDraft(group.name); setRenaming(true) }}
            className="text-sm font-semibold text-foreground hover:underline" title="Click to rename">
            {group.name}
          </button>
        )}

        <span className="text-xs text-muted-foreground tabular-nums">{group.sources.length}</span>
        <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="text-success">{enabledCount} on</span>
          {errorCount > 0 && <span className="text-destructive">{errorCount} err</span>}
        </div>
      </div>

      {/* Source list */}
      {!collapsed && group.sources.map((s) => (
        <SourceRow key={s.id} source={s} expanded={expanded.has(s.id)} checked={selected.has(s.id)}
          onToggleExpand={() => onToggleExpand(s.id)} onToggleCheck={() => onToggleCheck(s.id)} />
      ))}
    </Card>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────

export default function Sources() {
  const qc = useQueryClient()
  const { data, isLoading, error, isFetching } = useSources()
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')
  const runAll = useMutation({
    mutationFn: () => api.runAllSources(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['sources'] }); void qc.invalidateQueries({ queryKey: ['job-runs'] }) },
  })
  const reseed = useMutation({
    mutationFn: () => api.reseedSources(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['sources'] }) },
  })

  const allGroups = useMemo(() => groupByKind(data ?? []), [data])

  // Filter groups by search term (slug, display_name, tags)
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allGroups
    return allGroups
      .map((g) => ({
        ...g,
        sources: g.sources.filter(
          (s) =>
            s.slug.toLowerCase().includes(q) ||
            s.display_name.toLowerCase().includes(q) ||
            (s.tags ?? []).some((t) => t.toLowerCase().includes(q)),
        ),
      }))
      .filter((g) => g.sources.length > 0)
  }, [allGroups, search])

  const toggle = (set: Set<number>, id: number) => {
    const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); return n
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Sources</h1>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sources..."
            className="w-36 sm:w-44"
          />
          {isFetching && <span className="text-xs text-muted-foreground animate-pulse">syncing...</span>}
          <Button type="button" variant="primary" size="sm" loading={runAll.isPending}
            onClick={() => runAll.mutate()}
            className="bg-foreground text-background hover:opacity-80">
            <Play size={12} /> Run All
          </Button>
          <Button type="button" variant="ghost" size="sm" loading={reseed.isPending}
            onClick={() => { if (confirm('Delete all sources and reseed from defaults? Items will also be deleted.')) reseed.mutate() }}>
            Reseed
          </Button>
        </div>
      </div>

      {selected.size > 0 && <BulkBar selected={selected} sources={data ?? []} onClear={() => setSelected(new Set())} />}
      {error && <ErrorBanner error={error} />}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => (
            <GroupSection key={g.name} group={g} expanded={expanded} selected={selected}
              onToggleExpand={(id) => setExpanded((p) => toggle(p, id))}
              onToggleCheck={(id) => setSelected((p) => toggle(p, id))}
              onToggleGroupCheck={(ids, checked) => setSelected((prev) => {
                const n = new Set(prev); ids.forEach((id) => checked ? n.add(id) : n.delete(id)); return n
              })} />
          ))}
        </div>
      )}
    </div>
  )
}
